import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { ScrollView } from 'react-native';
import {
  BodyText,
  Button,
  DisplayText,
  Modal,
  ModalRow,
  theme,
  useResponsive,
  VStack,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { PaymentMethod } from './useSweetpassPaymentMethodEdit';

export const SweetpassPaymentMethodEditScreenContent = ({
  testID,
  hasActiveSubscription,
  paymentMethod,
  pressSetActiveHandler,
  isSettingActive,
  pressRemoveHandler,
  isRemoving,
}: SweetpassPaymentMethodEditScreenContentProps) => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();

  const isBusy = isSettingActive || isRemoving;
  const isUseButtonDisabled = isBusy && !isSettingActive;
  const isRemoveButtonDisabled = isBusy && !isRemoving;

  return (
    <ScrollView testID={testID}>
      <ModalRow>
        <DisplayText
          size={match([1, 3])}
          style={styles.header}
          testID={testID?.concat('.headline')}
        >
          {paymentMethod?.cardType}
        </DisplayText>
      </ModalRow>
      <Modal.BodyText testID={testID?.concat('.body')}>
        {t('account.sweetpass-membership.payment-methods.credit-card', {
          lastFour: paymentMethod?.lastFour,
          expiresAt: `${
            paymentMethod?.expirationMonth
          }/${paymentMethod?.expirationYear?.slice(-2)}`,
        })}
      </Modal.BodyText>
      <Modal.Footer testID={testID?.concat('.footer')}>
        {paymentMethod?.isActive ? (
          <BodyText
            testID={testID?.concat('.active-message')}
            style={styles.activeMessage}
            size={3}
          >
            {t(
              'account.sweetpass-membership.payment-methods.method-active-message',
            )}
          </BodyText>
        ) : (
          <VStack customSpacing={theme.spacing.medium}>
            {hasActiveSubscription ? (
              <Button
                testID={testID?.concat('.set-active-cta')}
                size="large"
                palette="primary"
                onPress={pressSetActiveHandler}
                isLoading={isSettingActive}
                disabled={isUseButtonDisabled}
              >
                {t(
                  'account.sweetpass-membership.payment-methods.set-active-cta.title',
                )}
              </Button>
            ) : null}
            <Button
              testID={testID?.concat('.remove-cta')}
              size="large"
              palette="caution"
              onPress={pressRemoveHandler}
              isLoading={isRemoving}
              disabled={isRemoveButtonDisabled}
            >
              {t(
                'account.sweetpass-membership.payment-methods.remove-cta.title',
              )}
            </Button>
          </VStack>
        )}
      </Modal.Footer>
    </ScrollView>
  );
};

// –––––––––––––––––––––––––––––––––––––––––––––––––––––

type SweetpassPaymentMethodEditScreenContentProps = {
  readonly hasActiveSubscription: boolean;
  readonly paymentMethod: PaymentMethod;
  readonly pressSetActiveHandler: () => void;
  readonly isSettingActive: boolean;
  readonly pressRemoveHandler: () => void;
  readonly isRemoving: boolean;
} & Pick<ViewProps, 'testID'>;

// –––––––––––––––––––––––––––––––––––––––––––––––––––––

const styles = StyleSheet.create({
  activeMessage: {
    color: theme.colors.NEUTRAL_2,
  },
  header: {
    paddingVertical: theme.spacing.large,
    marginBottom: theme.spacing.large,
  },
});
